/**
 * MarketplaceForm - Formulário para criar anúncio no marketplace
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Criação de anúncio de produto
 * - Validação de limites do plano
 * - Upload de imagens via ImageManager
 * - Campos: título, categoria, preço, condição, localização, descrição, contato
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card, Button, Input, Label, Textarea (shadcn/ui)
 * - ImageManager (componente extraído)
 */

import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useTranslation } from "../translations";
import ImageManager from "./ImageManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarketplaceForm = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: "", description: "", category: "", price: "", location: "",
    contact_info: "", condition: "novo", images: []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert(`❌ ${t('needLoginToSell')}`);
        setLoading(false);
        return;
      }

      // Configure headers with token
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Check plan limits first
      const limitsResponse = await axios.get(`${API}/check-plan-limits/create_marketplace_item`, config);
      
      if (!limitsResponse.data.allowed) {
        alert(`❌ ${limitsResponse.data.message}\n\n${t('considerUpgrade')}`);
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        // Converte formato brasileiro (42.000,00) para número
        // Remove pontos de milhar, substitui vírgula por ponto decimal
        price: parseFloat(String(formData.price).replace(/\./g, '').replace(',', '.')),
        images: Array.isArray(formData.images) ? formData.images : 
               (formData.images ? formData.images.split('\n').filter(url => url.trim()) : [])
      };
      await axios.post(`${API}/marketplace`, submitData, config);
      setFormData({
        title: "", description: "", category: "", price: "", location: "",
        contact_info: "", condition: "novo", images: []
      });
      onUpdate();
      alert(t('productRegisteredSuccess'));
    } catch (error) {
      console.error("Error creating ad:", error);
      alert(`${t('errorRegisteringProduct')}: ` + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('advertiseProduct')}</CardTitle>
        <CardDescription>{t('registerProductForSale')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">{t('productName')} *</Label>
              <Input 
                id="title" 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
                required 
              />
            </div>
            <div>
              <Label htmlFor="category">{t('category')} *</Label>
              <select 
                id="category"
                value={formData.category} 
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                required
              >
                <option value="">{t('selectCategory')}</option>
                <option value="propriedades">{t('properties')}</option>
                <option value="ferramentas">{t('tools')}</option>
                <option value="insumos">{t('supplies')}</option>
                <option value="defensivos">{t('pesticides')}</option>
                <option value="maquinaria">{t('machinery')}</option>
                <option value="outros">{t('others')}</option>
              </select>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="price">{t('price')} *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
                <Input 
                  id="price" 
                  type="text"
                  inputMode="decimal"
                  className="pl-10"
                  value={formData.price} 
                  onChange={(e) => {
                    // Aceita vírgula ou ponto como separador decimal
                    let value = e.target.value;
                    // Permite apenas números, vírgula e ponto
                    value = value.replace(/[^0-9.,]/g, '');
                    setFormData({...formData, price: value});
                  }}
                  onBlur={(e) => {
                    // Ao sair do campo, normaliza o valor
                    let value = e.target.value.replace(',', '.');
                    if (value && !isNaN(parseFloat(value))) {
                      setFormData({...formData, price: parseFloat(value).toFixed(2)});
                    }
                  }}
                  placeholder="42.000,00"
                  required 
                />
              </div>
            </div>
            <div>
              <Label htmlFor="condition">{t('condition')} *</Label>
              <select 
                id="condition"
                value={formData.condition} 
                onChange={(e) => setFormData({...formData, condition: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                required
              >
                <option value="novo">{t('conditionNew')}</option>
                <option value="seminovo">{t('semiNew')}</option>
                <option value="usado">{t('conditionUsed')}</option>
              </select>
            </div>
            <div>
              <Label htmlFor="location">{t('fullLocation')} *</Label>
              <Input 
                id="location" 
                value={formData.location} 
                onChange={(e) => setFormData({...formData, location: e.target.value})} 
                required 
                placeholder={t('locationPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Seja específico: Cidade, Estado, CEP, referências (facilita encontrar)
              </p>
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">Descrição do Produto *</Label>
            <Textarea 
              id="description" 
              value={formData.description} 
              onChange={(e) => setFormData({...formData, description: e.target.value})} 
              required 
              rows={4}
              placeholder="Descreva detalhes do produto, condições, especificações técnicas..."
            />
          </div>
          
          <div>
            <Label htmlFor="contact_info">Informações de Contato *</Label>
            <Input 
              id="contact_info" 
              value={formData.contact_info} 
              onChange={(e) => setFormData({...formData, contact_info: e.target.value})} 
              required 
              placeholder="Telefone, WhatsApp, email..."
            />
          </div>

          <div>
            <Label>Fotos e Vídeos do Produto</Label>
            <ImageManager
              images={formData.images || []}
              onImagesChange={(images) => {
                setFormData({...formData, images: images});
              }}
            />
          </div>
          
          <Button type="submit" disabled={loading}>
            {loading ? "Publicando..." : "Publicar Anúncio"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default MarketplaceForm;
